import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStrategies } from '../../../context/UserStrategies';
import { COLORS_HEX, COLORS } from '../../../constants/colors';
import { IStrategy } from '../../../types/model';
import StrategySelect from '../../Strategies/StrategySelect';
import ColumnChart from './ColumnChart';
import RadialChart from './RadialChart';
import StrategyTable from './StrategyTable';
import { getTotalValues, truncateString } from '../../../helpers/functions';
import { GridWidget } from '../types';
import { Icon } from '../../UI';

const getPercentage = (value: number | undefined, wholeValue: number) => {
  return Math.round(((value || 0) / wholeValue) * 100);
};

const PortfolioOverlapWidget = ({ width, height }: GridWidget) => {
  const { strategies } = useUserStrategies();
  const { t } = useTranslation();

  const initialStrategy_A =
    strategies.find(({ active }) => active) ?? strategies[0];

  const initialStrategy_B = strategies.find(
    (s) => s.id !== initialStrategy_A.id
  );

  const [strategy_A, setStrategy_A] = useState<IStrategy | undefined>(
    initialStrategy_A
  );
  const [strategy_B, setStrategy_B] = useState<IStrategy | undefined>(
    initialStrategy_B
  );

  const [selectedStrategy, setSelectedStrategy] = useState<IStrategy>();
  const [filterType, setFilterType] = useState<'match' | 'unique'>('match');

  const [strategyA, strategyB] = useMemo(() => {
    return [
      strategies.find(({ id }) => id === strategy_A?.id),
      strategies.find(({ id }) => id === strategy_B?.id),
    ];
  }, [strategies, strategy_A, strategy_B]);

  const commonAssets = useMemo(() => {
    if (!strategyA || !strategyB) {
      return [];
    }

    return strategyA?.strategyAssets.items.filter(
      ({ asset: { id } }) =>
        strategyB?.strategyAssets.items.findIndex(
          ({ asset: { id: targetId } }) => id === targetId
        ) !== -1
    );
  }, [strategyA, strategyB]);

  const uniqueAssets = useMemo(() => {
    if (!strategyA || !strategyB) {
      return [];
    }

    return strategyA?.strategyAssets.items
      .filter(
        ({ asset: { id } }) =>
          strategyB?.strategyAssets.items.findIndex(
            ({ asset: { id: targetId } }) => id === targetId
          ) === -1
      )
      .concat(
        strategyB?.strategyAssets.items.filter(
          ({ asset: { id } }) =>
            strategyA?.strategyAssets.items.findIndex(
              ({ asset: { id: targetId } }) => id === targetId
            ) === -1
        )
      );
  }, [strategyA, strategyB]);

  const currency = strategyA?.currency.toLowerCase() as 'eur' | 'usd';

  const strategyAValue = getTotalValues(
    strategyA?.strategyAssets?.items || [],
    currency,
    strategyA!.createdAt
  );

  const strategyBValue = getTotalValues(
    strategyB?.strategyAssets?.items || [],
    currency,
    strategyB!.createdAt
  );

  const common1Percentage = getPercentage(
    (strategyA?.strategyAssets?.items || [])
      .filter(({ asset: { id } }) =>
        commonAssets?.find(({ asset: { id: targetId } }) => id === targetId)
      )
      .reduce(
        (prev, current) =>
          prev + current.unit * (current?.asset?.history[0]?.[currency] ?? 0),
        0
      ),
    strategyAValue.totalValue || 0
  );

  const common2Percentage = getPercentage(
    (strategyB?.strategyAssets?.items || [])
      .filter(({ asset: { id } }) =>
        commonAssets?.find(({ asset: { id: targetId } }) => id === targetId)
      )
      .reduce(
        (prev, current) =>
          prev + current.unit * (current?.asset?.history[0]?.[currency] ?? 0),
        0
      ),
    strategyBValue.totalValue || 0
  );

  const { overlapPercentage, overlapValue, commonValue1, commonValue2 } =
    useMemo(() => {
      if (!strategyA || !strategyB || !commonAssets.length) {
        return {
          overlapPercentage: 0,
          overlapValue: 0,
          commonValue1: undefined,
          commonValue2: undefined,
        };
      }

      const commonValue1 = strategyA.strategyAssets.items
        .filter(({ asset: { id } }) =>
          commonAssets?.find(({ asset: { id: targetId } }) => id === targetId)
        )
        .reduce(
          (prev, current) =>
            prev + current.unit * (current?.asset?.history[0]?.[currency] ?? 0),
          0
        );

      const commonValue2 = strategyB.strategyAssets.items
        .filter(({ asset: { id } }) =>
          commonAssets?.find(({ asset: { id: targetId } }) => id === targetId)
        )
        .reduce(
          (prev, current) =>
            prev + current.unit * (current?.asset?.history[0]?.[currency] ?? 0),
          0
        );

      const CurrencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });

      return {
        commonValue1,
        commonValue2,
        ...(commonValue1 > commonValue2
          ? {
              overlapPercentage: getPercentage(commonValue2, commonValue1),
              overlapValue: CurrencyFormatter.format(commonValue2),
            }
          : {
              overlapPercentage: getPercentage(commonValue1, commonValue2),
              overlapValue: CurrencyFormatter.format(commonValue1),
            }),
      };
    }, [strategyA, strategyB, commonAssets]);

  const radialChartData = [
    {
      id: 'strategyA',
      data: [
        {
          x: strategyA!.name,
          y: common1Percentage,
        },
      ],
    },
    {
      id: 'strategyB',
      data: [
        {
          x: strategyB!.name,
          y: common2Percentage,
        },
      ],
    },
  ];

  const dropdownStrategies = strategies?.filter(
    ({ id }) => id !== strategy_B?.id && id !== strategy_A?.id
  );

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-grey-90 overflow-hidden">
      <div className="flex w-full items-center py-3 border-b border-grey-10 dark:border-grey-70">
        <StrategySelect
          className={'ml-4'}
          strategies={dropdownStrategies}
          strategy={strategyA}
          setStrategy={setStrategy_A}
          color={strategyA!.color}
        />
        <Icon svgName={'overlap'} className={'mx-3'} />
        <StrategySelect
          strategies={dropdownStrategies}
          strategy={strategyB}
          setStrategy={setStrategy_B}
          color={strategyB!.color}
        />
      </div>

      <div
        className={`
          ${
            width > 1
              ? 'flex h-full divide-x-1 divide-grey-10 dark:divide-grey-70'
              : 'flex flex-col'
          }
        `}
      >
        <div
          className={`
            ${width === 1 && selectedStrategy ? 'hidden' : ''}
            relative
            flex
            w-full
            min-h-80
            ${width > 1 && height === 2 ? 'pt-8' : 'pt-0'}
          `}
        >
          {/* LEFT COL */}
          <div
            className={`
              ${
                height === 2 && width === 3
                  ? 'ml-4 pl-4 pt-8'
                  : width === 3
                  ? 'm-2 ml-4 pl-20 py-4 '
                  : width === 2
                  ? 'm-2 pl-8 pt-4'
                  : 'm-2 pl-4 pt-4'
              }
              ${width > 1 && height === 2 ? 'mt-16 mb-4 pb-16' : 'mt-6 mb-4'}
              ${width > 1 && height === 2 ? 'h-180' : 'h-70'}
              border-${COLORS[strategyA!.color]}
              absolute
              w-2/3
              flex
              justify-start
              left-0
              top-1
              border
              rounded-xl
            `}
          >
            <div className="text-center flex flex-col items-center">
              <ColumnChart
                width={width}
                onClick={() => {
                  setSelectedStrategy(strategyA);
                  setFilterType('unique');
                }}
                height={height}
                percentage={100 - common1Percentage}
                backgroundColor={COLORS[strategyA!.color]}
              />
              {strategyA && (
                <div className="uppercase text-grey-50">
                  {truncateString(strategyA.name, 9)}
                  <div className={'text-xs'}>
                    {t('widget.portfolioOverlap.only')}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MIDDLE COL */}
          <div
            className={`absolute ${
              width === 3
                ? 'w-1/3 left-1/3 justify-start'
                : width === 2
                ? 'w-1/2 left-1/4 justify-center'
                : 'w-full m-auto'
            }
            ${width > 1 && height === 2 ? 'pt-16' : 'pt-12'}
             px-2 text-center`}
          >
            <div className="flex justify-center">
              <ColumnChart
                width={width}
                percentage={common1Percentage}
                backgroundColor={COLORS[strategyA!.color]}
                height={height}
                onClick={() => {
                  setSelectedStrategy(strategyA);
                  setFilterType('match');
                }}
              />
              <ColumnChart
                width={width}
                percentage={common2Percentage}
                backgroundColor={COLORS[strategyB!.color]}
                height={height}
                onClick={() => {
                  setSelectedStrategy(strategyB);
                  setFilterType('match');
                }}
              />
            </div>
            {strategyA && strategyB && (
              <div className="uppercase text-grey-50">
                {t('widget.portfolioOverlap.commonAssets')}
              </div>
            )}
          </div>

          {/* RIGHT COL */}
          <div
            className={`absolute mt-3 flex justify-end right-0 ${
              height === 2 && width === 3
                ? 'mx-3 pr-4'
                : width === 3
                ? 'mx-3 pr-20'
                : width === 2
                ? 'mx-2 pr-8'
                : 'mx-2 pr-4'
            }
            w-2/3
            overflow-hidden
            ${width > 1 && height === 2 ? 'h-180' : 'h-70'}
            ${width > 1 && height === 2 ? 'pt-16 pb-16' : 'pt-8 pb-4'}
            border border-${COLORS[strategyB!.color]} rounded-xl`}
          >
            <div className={`text-center flex flex-col items-center`}>
              <ColumnChart
                width={width}
                height={height}
                onClick={() => {
                  setSelectedStrategy(strategyB);
                  setFilterType('unique');
                }}
                percentage={100 - common2Percentage}
                backgroundColor={COLORS[strategyB!.color]}
              />
              {strategyB && (
                <div className="uppercase text-grey-50">
                  {truncateString(strategyB.name, 9)}
                  <div className={'text-xs'}>
                    {t('widget.portfolioOverlap.only')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RADIAL CHART */}
        <div
          className={`
              ${
                width === 1 && height === 1
                  ? 'hidden'
                  : width === 1 && height === 2
                  ? 'w-full'
                  : 'w-88 relative'
              }
              flex-none
              h-full
              overflow-y-auto
              sticky
              top-0
              left-0
            `}
        >
          {selectedStrategy ? (
            <div
              className={`
              ${width > 1 ? 'min-w-88 w-88 h-full' : 'w-full h-full'}
            `}
            >
              <StrategyTable
                strategy={selectedStrategy}
                filterType={filterType}
                onBack={() => setSelectedStrategy(undefined)}
                assetIds={commonAssets.map(
                  (strategyAsset) => strategyAsset.asset.id
                )}
              />
            </div>
          ) : (
            <div className={'h-full w-full relative'}>
              <div
                className={`
                  flex
                  justify-center
                  items-center
                  absolute
                  top-0
                  bottom-0
                  left-0
                  right-0
                  oveflow-hidden
                  bg-center
                  z-20
                `}
              >
                <div className={'relative w-full h-80'}>
                  <RadialChart
                    colors={[
                      COLORS_HEX[strategyA!.color],
                      COLORS_HEX[strategyB!.color],
                    ]}
                    data={radialChartData}
                  />
                </div>
              </div>
              <div className="flex flex-col items-center justify-center absolute top-1/2 left-1/2 right-1/2 bottom-1/2 z-30">
                <div className={'flex flex-col items-center w-52 '}>
                  <p className="text-grey-50">
                    {t('widget.portfolioOverlap.portfolioOverlap')}
                  </p>
                  <p className="text-3xl font-bold flex">
                    {overlapPercentage}%
                  </p>
                  <p className="cursor-default text-sm text-center">
                    ( {overlapValue} )
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioOverlapWidget;
